<template>
    <header-leafly />

    <section class="category-banner" style="background-image: url('/leaflypackaging-com/images/services/banner.jpg');">
        <div class="container mx-auto px-4 h-[100%] flex flex-col justify-center">
            <h1 class="leafly-page-title">Comprehensive Custom Packaging Solutions — From Concept to Delivery</h1>
            <p class="leafly-page-second-title max-w-[680px]">Professional packaging services including strategic design, prototype validation, optimized manufacturing, quality control, and logistics — all tailored to elevate your brand, reduce costs, and streamline production.</p>
        </div>
    </section>

    <section class="py-16">
        <div class="lg:container mx-auto px-4">
            <div class="service-intro-section" data-aos="fade-up">
                <div class="intro-content">
                    <h2 class="intro-title">Professional & Reliable</h2>
                    <p class="intro-text">
                        From concept to delivery, Leafly Packaging supports you every step of the way.
                    </p>
                    <p class="intro-text">
                        Our streamlined service process is designed to make custom packaging simple, efficient, and stress-free. Whether you need a fully developed packaging solution or production based on existing artwork, our team works closely with you to ensure your packaging reflects your brand while meeting functional and sustainability requirements.
                    </p>
                    <p class="intro-text">
                        Throughout production, our strict quality control procedures are applied at every stage to ensure consistency, accuracy, and high standards.
                    </p>
                    <p class="intro-text">
                        Even after your order leaves our facility, our support continues. Our experienced logistics team coordinates shipping and delivery to the United States and Canada, ensuring your products arrive safely and on time.
                    </p>
                    <p class="intro-text">
                        We're committed to building long-term partnerships through dependable service and consistent quality.
                    </p>
                </div>
                <div class="intro-image">
                    <img src="/leaflypackaging-com/images/services/service.jpg" alt="Service Structure" />
                </div>
            </div>

            <div class="service-process-section">
                <h2 class="process-main-title">How to Order Custom Packaging</h2>
                <p class="process-description">We make custom packaging simple. Follow the steps below to get started.</p>
                
                <div class="steps-grid">
                    <div class="step-card" data-aos="fade-up" data-aos-delay="100">
                        <div class="step-header-bar">
                            <div class="step-number">1</div>
                            <h3 class="step-title">Send Us Your Requirements</h3>
                        </div>
                        <div class="step-image-container">
                            <img src="/leaflypackaging-com/images/services/service-step1.jpg" alt="Send Requirements" class="step-image" />
                            <div class="step-overlay">
                                <div class="step-overlay-content">
                                    <p class="overlay-intro">Please email us the following information:</p>
                                    <ul class="overlay-list">
                                        <li>📦&nbsp&nbsp Packaging type (box, mailer box, sticker, label, etc.)</li>
                                        <li>📏&nbsp&nbsp  Size (Length × Width × Height)</li>
                                        <li>📄&nbsp&nbsp  Material (corrugated, paperboard, kraft, etc.)</li>
                                        <li>🎨&nbsp&nbsp  Printing (CMYK, Pantone, inside/outside?)</li>
                                        <li>✨&nbsp&nbsp  Finish (matte, gloss, UV, foil stamping, etc.)</li>
                                        <li>📊&nbsp&nbsp  Quantity</li>
                                        <li>🚚&nbsp&nbsp  Shipping address</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="step-card" data-aos="fade-up" data-aos-delay="200">
                        <div class="step-header-bar">
                            <div class="step-number">2</div>
                            <h3 class="step-title">Get a Quote</h3>
                        </div>
                        <div class="step-image-container">
                            <img src="/leaflypackaging-com/images/services/service-step2.jpg" alt="Get Quote" class="step-image" />
                            <div class="step-overlay">
                                <div class="step-overlay-content">
                                    <p class="overlay-intro">Once we receive your details, we will:</p>
                                    <ul class="overlay-list">
                                        <li>✓&nbsp&nbsp  Review your requirements</li>
                                        <li>✓&nbsp&nbsp  Confirm technical details if needed</li>
                                        <li>✓&nbsp&nbsp  Provide estimated production lead time</li>
                                        <li>✓&nbsp&nbsp  Calculate shipping cost (if required)</li>
                                        <li>✓&nbsp&nbsp  Send you a formal quotation(in 24 hours)</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="step-card" data-aos="fade-up" data-aos-delay="300">
                        <div class="step-header-bar">
                            <div class="step-number">3</div>
                            <h3 class="step-title">Confirm Artwork</h3>
                        </div>
                        <div class="step-image-container">
                            <img src="/leaflypackaging-com/images/services/service-step5.jpg" alt="Confirm Artwork" class="step-image" />
                            <div class="step-overlay">
                                <div class="step-overlay-content">
                                    <p class="overlay-intro">After pricing is approved:</p>
                                    <ul class="overlay-list">
                                        <li>✓&nbsp&nbsp You send us your editable AI/PDF artwork file</li>
                                        <li>✓&nbsp&nbsp OR we can help adjust your design</li>
                                        <li>✓&nbsp&nbsp We will send a <strong>digital proof</strong> for approval before production</li>
                                    </ul>
                                    <p class="overlay-note">⚠ Production will only begin after artwork approval.</p>
                                </div>
                            </div>
                        </div>
                    </div>


                    <div class="step-card" data-aos="fade-up" data-aos-delay="400">
                        <div class="step-header-bar">
                            <div class="step-number">4</div>
                            <h3 class="step-title">Sampling</h3>
                        </div>
                        <div class="step-image-container">
                            <img src="/leaflypackaging-com/images/services/service-step6.jpg" alt="Sampling" class="step-image" />
                            <div class="step-overlay">
                                <div class="step-overlay-content">
                                    <p class="overlay-intro">Before mass production, we can provide:</p>
                                    <ul class="overlay-list">
                                        <li>✓&nbsp&nbsp Digital sample (free)</li>
                                        <li>✓&nbsp&nbsp Physical sample (if required, sample fee may apply)</li>
                                    </ul>
                                    <p class="overlay-note">Once the sample is approved and confirmed with no issues, we proceed to mass production.</p>
                                </div>
                            </div>
                        </div>
                    </div>


                    <div class="step-card collaboration-card" style="height:328px;" data-aos="fade-up" data-aos-delay="400">
                        <div class="collaboration-background">
                            <div class="collaboration-overlay"></div>
                            <div class="collaboration-content">
                                <div class="collaboration-icon">🤝</div>
                                <h3 class="collaboration-title">Leafly Packaging Services</h3>
                                <p class="collaboration-subtitle">Professional Partnership · Customized Solutions · Quality Assurance</p>
                            </div>
                        </div>
                    </div>

                    <div class="step-card" data-aos="fade-up" data-aos-delay="500">
                        <div class="step-header-bar">
                            <div class="step-number">5</div>
                            <h3 class="step-title">Make Payment</h3>
                        </div>
                        <div class="step-image-container">
                            <img src="/leaflypackaging-com/images/services/service-step3.jpg" alt="Make Payment" class="step-image" />
                            <div class="step-overlay">
                                <div class="step-overlay-content">
                                    <p class="overlay-intro">For first-time customers:</p>
                                    <ul class="overlay-list">
                                        <li>✓&nbsp&nbsp 50% deposit before production</li>
                                        <li>✓&nbsp&nbsp 50% balance before shipment</li>
                                    </ul>
                                    <p class="overlay-note">We accept Wire Transfer / Paypal</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="step-card" data-aos="fade-up" data-aos-delay="700">
                        <div class="step-header-bar">
                            <div class="step-number">6</div>
                            <h3 class="step-title">Mass Production</h3>
                        </div>
                        <div class="step-image-container">
                            <img src="/leaflypackaging-com/images/services/service-step7.jpg" alt="Production" class="step-image" />
                            <div class="step-overlay">
                                <div class="step-overlay-content">
                                    <p class="overlay-intro">Before mass production, we can provide:</p>
                                    <ul class="overlay-list">
                                        <li>✓&nbsp&nbsp Standard production time: 10–15 business days</li>
                                        <li>✓&nbsp&nbsp Large volume orders may require additional time</li>
                                    </ul>
                                    <p class="overlay-note">We will provide production updates as needed.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="step-card" data-aos="fade-up" data-aos-delay="700">
                        <div class="step-header-bar">
                            <div class="step-number">7</div>
                            <h3 class="step-title">Shipping</h3>
                        </div>
                        <div class="step-image-container">
                            <img src="/leaflypackaging-com/images/services/service-step8.jpg" alt="Shipping" class="step-image" />
                            <div class="step-overlay">
                                <div class="step-overlay-content">
                                    <p class="overlay-intro">We offer:</p>
                                    <ul class="overlay-list">
                                        <li>✓ Air freight (fast delivery | 8-15 days)</li>
                                        <li>✓ Sea freight (cost-effective for bulk orders | 18-25 days)</li>
                                        <li>✓ Express (FedEx / UPS / DHL | 3-5 days)</li>
                                    </ul>
                                    <p class="overlay-note">Tracking number will be provided once shipped.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="step-card" data-aos="fade-up" data-aos-delay="800">
                        <div class="step-header-bar">
                            <div class="step-number">8</div>
                            <h3 class="step-title">Receive Your Order</h3>
                        </div>
                        <div class="step-image-container">
                            <img src="/leaflypackaging-com/images/services/service-step9.jpg" alt="Receive Your Order" class="step-image" />
                            <div class="step-overlay">
                                <div class="step-overlay-content">
                                    <ul class="overlay-list">
                                        <li>✓ Inspect your packaging upon arrival</li>
                                        <li>✓ If there are any issues, contact us within 7 days and we will resolve them quickly</li>
                                    </ul>
                                    <p class="overlay-note">We value long-term partnerships.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    </section>

    <section class="py-16 bg-white">
        <div class="lg:container mx-auto px-4">
            <div class="development-process-section">
                <h2 class="process-main-title">Packaging Solution Development Process</h2>
                <p class="process-description">Don't have artwork? No problem! Our design team provides full artwork support. </p>
                <p class="process-description mb-[48px]">From concept to final dieline – ensuring your packaging perfectly represents your brand.</p>
                
                <div class="timeline-steps">
                    <div class="timeline-step" data-aos="fade-up" data-aos-delay="100">
                        <div class="timeline-number">1</div>
                        <div class="timeline-content">
                            <div class="timeline-image">
                                <img src="/leaflypackaging-com/images/services/solution-step1.jpg"  alt="Consultation" />
                            </div>
                            <div class="timeline-text">
                                <h3 class="timeline-title">Consultation</h3>
                                <p class="timeline-desc">Let's Talk About Your Custom Packaging Goals</p>
                                <p class="timeline-detail">Whether you need custom shipping packaging or custom made packaging for luxury retail in Europe, the U.S., Australia and beyond, our team helps you find the right solution.</p>
                            </div>
                        </div>
                    </div>

                    <div class="timeline-step" data-aos="fade-up" data-aos-delay="200">
                        <div class="timeline-number">2</div>
                        <div class="timeline-content">
                            <div class="timeline-image">
                                <img src="https://images.leaflypackaging.com/profile/upload/2025/12/26/5_20251226151735A006.webp" alt="Structural Design" />
                            </div>
                            <div class="timeline-text">
                                <h3 class="timeline-title">Custom Packaging Structural Design</h3>
                                <p class="timeline-desc">Design That Protects & Impresses</p>
                                <p class="timeline-detail">Our engineers craft functional and beautiful custom product packaging tailored to your needs.</p>
                            </div>
                        </div>
                    </div>

                    <div class="timeline-step" data-aos="fade-up" data-aos-delay="300">
                        <div class="timeline-number">3</div>
                        <div class="timeline-content">
                            <div class="timeline-image">
                                <img src="/leaflypackaging-com/images/services/solution-step3.jpg" alt="Material Selection" />
                            </div>
                            <div class="timeline-text">
                                <h3 class="timeline-title">Packaging Material Selection</h3>
                                <p class="timeline-desc">Select materials from different meterials for impressive packaging</p>
                                <p class="timeline-detail">We recommend the best material based on your branding and budget – whether your packaging requires durability, product protection, moisture resistance, or premium shelf presentation.</p>
                            </div>
                        </div>
                    </div>

                    <div class="timeline-step" data-aos="fade-up" data-aos-delay="400">
                        <div class="timeline-number">4</div>
                        <div class="timeline-content">
                            <div class="timeline-image">
                                <img src="https://images.leaflypackaging.com/profile/upload/2025/12/26/2_20251226142040A003.webp" alt="Artwork Support" />
                            </div>
                            <div class="timeline-text">
                                <h3 class="timeline-title">Artwork & Dieline Support</h3>
                                <p class="timeline-desc">Already have your artwork and packaging specifications ready?</p>
                                <p class="timeline-detail">If your packaging dimensions, structure, materials, functionality, and finishing options are already defined, we’ll finalize the dieline and prepare everything for production.</p>
                            </div>
                        </div>
                    </div>

                    <div class="timeline-step" data-aos="fade-up" data-aos-delay="500">
                        <div class="timeline-number">5</div>
                        <div class="timeline-content">
                            <div class="timeline-image">
                                <img src="/leaflypackaging-com/images/services/solution-step5.jpg" alt="Finishing Touches" />
                            </div>
                            <div class="timeline-text">
                                <h3 class="timeline-title">Packaging Finishing Touches</h3>
                                <p class="timeline-desc">Add your brand's personality with custom logos, foil stamping, UV coating, embossing & debossing</p>
                                <p class="timeline-detail">Our team will help you choose the right printing and finishing techniques to elevate your custom packaging with logo into a memorable brand experience.</p>
                            </div>
                        </div>
                    </div>

                    <div class="timeline-step" data-aos="fade-up" data-aos-delay="600">
                        <div class="timeline-number">6</div>
                        <div class="timeline-content">
                            <div class="timeline-image">
                                <img src="https://images.leaflypackaging.com/profile/upload/2025/12/26/3_20251226144717A004.webp" alt="Sampling & Production" />
                            </div>
                            <div class="timeline-text">
                                <h3 class="timeline-title">Sampling & Production</h3>
                                <p class="timeline-desc">Test It. Love It. Then Go Big.</p>
                                <p class="timeline-detail">We'll create physical samples to ensure your packaging is perfect before we move into full production. From small MOQs (1,000 pcs) to high-volume runs – we've got you covered.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>


    <section class="py-16 bg-white">
        <div class="lg:container mx-auto px-4">
            <div class="logistics-service-section">
                <h2 class="process-main-title">Logistics Services</h2>
                <p class="process-description">At Leafly Packaging, we understand that reliable logistics are just as important as high-quality packaging. Whether you're ordering samples or large-volume production, we offer flexible shipping solutions to meet your timeline and budget.</p>
                <p class="shipping-intro">We provide three main shipping options for our customers:</p>
                <div class="shipping-options-grid">

                    <div class="shipping-card" data-aos="fade-up" data-aos-delay="300">
                        <div class="shipping-icon">📦</div>
                        <h3 class="shipping-title">Express Shipping</h3>
                        <p class="shipping-subtitle">Fastest Delivery | 3–5 Days</p>
                        <p class="shipping-description">Express shipping (FedEx / UPS / DHL) is the quickest option available and is commonly used for samples and small orders.</p>
                        
                        <div class="shipping-details">
                            <h4 class="details-heading">Best for:</h4>
                            <ul class="details-list">
                                <li>Packaging samples</li>
                                <li>Urgent small-batch orders</li>
                                <li>Pre-production approvals</li>
                                <li>Tight launch deadlines</li>
                            </ul>

                            <h4 class="details-heading">Advantages:</h4>
                            <ul class="details-list">
                                <li>Door-to-door service</li>
                                <li>Fast customs clearance</li>
                                <li>Real-time tracking</li>
                                <li>Minimal handling</li>
                            </ul>

                            <p class="shipping-note">If you need samples before mass production or have a small but urgent order, express shipping ensures you receive your packaging as quickly as possible.</p>
                        </div>
                    </div>

                    
                    <div class="shipping-card" data-aos="fade-up" data-aos-delay="100">
                        <div class="shipping-icon">✈️</div>
                        <h3 class="shipping-title">Air Freight</h3>
                        <p class="shipping-subtitle">Fast & Reliable | 8–15 Days</p>
                        <p class="shipping-description">Air freight is the ideal solution for mid-size orders that require faster delivery without the higher cost of express shipping.</p>
                        
                        <div class="shipping-details">
                            <h4 class="details-heading">Best for:</h4>
                            <ul class="details-list">
                                <li>Urgent production orders</li>
                                <li>Medium-volume shipments</li>
                                <li>Seasonal or time-sensitive packaging</li>
                            </ul>

                            <h4 class="details-heading">Advantages:</h4>
                            <ul class="details-list">
                                <li>Faster than sea freight</li>
                                <li>More cost-effective than express for larger shipments</li>
                                <li>Reliable transit schedules</li>
                                <li>Reduced inventory waiting time</li>
                            </ul>

                            <p class="shipping-note">If you need your packaging quickly but are shipping pallet quantities or bulk cartons, air freight offers the best balance between speed and cost.</p>
                        </div>
                    </div>

                    <div class="shipping-card featured" data-aos="fade-up" data-aos-delay="200">
                        <div class="featured-badge">Most Economical</div>
                        <div class="shipping-icon">🚢</div>
                        <h3 class="shipping-title">Sea Freight</h3>
                        <p class="shipping-subtitle">Cost-Effective for Bulk Orders | 18–25 Days</p>
                        <p class="shipping-description">Sea freight is the most economical shipping option for large-volume production orders.</p>
                        
                        <div class="shipping-details">
                            <h4 class="details-heading">Best for:</h4>
                            <ul class="details-list">
                                <li>High-volume packaging orders</li>
                                <li>Container shipments</li>
                                <li>Long-term inventory planning</li>
                            </ul>

                            <h4 class="details-heading">Advantages:</h4>
                            <ul class="details-list">
                                <li>Lowest shipping cost per unit</li>
                                <li>Ideal for full-container or large pallet shipments</li>
                                <li>Stable and predictable logistics planning</li>
                                <li>Perfect for established brands with forecasted demand</li>
                            </ul>

                            <p class="shipping-note">If you are ordering thousands of boxes or planning inventory for Amazon, retail stores, or warehouses in the U.S. or Canada, sea freight provides maximum cost efficiency.</p>
                        </div>
                    </div>

                   
                </div>

                <div class="shipping-guide" data-aos="fade-up">
                    <h3 class="guide-title">How to Choose the Right Shipping Method</h3>
                    <div class="guide-grid">
                        <div class="guide-item">
                            <span class="guide-label">For samples:</span>
                            <span class="guide-value">Express shipping is recommended.</span>
                        </div>
                        <div class="guide-item">
                            <span class="guide-label">For medium production orders:</span>
                            <span class="guide-value">Air freight provides the best speed-to-cost ratio.</span>
                        </div>
                        <div class="guide-item">
                            <span class="guide-label">For large bulk production:</span>
                            <span class="guide-value">Sea freight offers the most economical solution.</span>
                        </div>
                    </div>
                    <p class="guide-note">Our team will always recommend the most efficient shipping method based on your order size, delivery timeline, and budget.</p>
                </div>

                <div class="global-shipping" data-aos="fade-up">
                    <h3 class="global-title">Reliable Global Shipping</h3>
                    <p class="global-description">Leafly Packaging has extensive experience exporting custom packaging to the United States and Canada. We work with trusted international freight partners to ensure:</p>
                    <div class="global-features">
                        <div class="global-feature">
                            <span class="feature-icon">✓</span>
                            <span class="feature-text">Secure packaging during transit</span>
                        </div>
                        <div class="global-feature">
                            <span class="feature-icon">✓</span>
                            <span class="feature-text">Accurate documentation</span>
                        </div>
                        <div class="global-feature">
                            <span class="feature-icon">✓</span>
                            <span class="feature-text">Smooth customs clearance</span>
                        </div>
                        <div class="global-feature">
                            <span class="feature-icon">✓</span>
                            <span class="feature-text">On-time delivery</span>
                        </div>
                    </div>
                    <p class="global-footer">From production to final delivery, we manage the logistics process to give you peace of mind.</p>
                </div>
            </div>
        </div>
    </section>

    <section class="py-16 bg-green-600 text-white">
        <div class="lg:container mx-auto px-4 text-center">
            <h2 class="text-3xl md:text-4xl font-weight-600 mb-4">Ready to Get Started?</h2>
            <p class="text-lg mb-8 max-w-2xl mx-auto">Contact us today and let's create the perfect packaging for your brand.</p>
            <a href="/contact-us" class="get-started-btn">CONTACT US NOW</a>
        </div>
    </section>

    <footer-index />
</template>

<script setup lang="ts" name="SinglePageServices">
    import type { SinglePage } from '~/types';
    import headerIndex from '../../components/header/header-index.vue'
    import footerIndex from '../../components/footer/footer-index.vue'
    import cmsConfig  from '~/cmsConfig'
    import headerLeafly from '../../components/header/header-leafly.vue'
    import cmsUtils from '~/utils/cmsUtils';
    const props = defineProps<{
      singlePage: SinglePage
    }>()

    const cmsInfo = await cmsUtils.getCmsInfo()
    const route = useRoute()
    const baseUrl = (cmsInfo.website.https ? 'https://' : 'http://') + cmsInfo.website.domain
    const canonicalUrl = baseUrl + route.path.replace('.html','')
    useHead({
        script: [
            {
                type: 'application/ld+json',
                innerHTML: JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'Service',
                    name: props.singlePage.title,
                    description: props.singlePage.description,
                    serviceType: 'Custom Packaging Solutions',
                    provider: {
                        '@type': 'Organization',
                        name: cmsInfo.website.title
                    },
                    areaServed: ['US', 'Canada'],
                    url: canonicalUrl
                })
            }
        ]
    })


</script>

<style scoped lang="scss">
    .category-banner {
        background-position: top;
        height:520px;
    }

    .service-intro-section {
        display: flex;
        gap: 48px;
        align-items: center;
        margin-bottom: 64px;

        @media (max-width: 1024px) {
            flex-direction: column;
            gap: 32px;
        }
    }

    .intro-content {
        flex: 1;
        max-width: 600px;
    }

    .intro-title {
        font-size: 36px;
        font-weight: 700;
        color: #0a6b2e;
        margin-bottom: 24px;
        line-height: 1.2;

        @media (max-width: 768px) {
            font-size: 28px;
        }
    }

    .intro-text {
        font-size: 20px;
        font-weight: 300;
        color: #000;
        margin-bottom: 16px;

        &:last-child {
            margin-bottom: 0;
        }
    }

    .intro-image {
        flex: 1;
        display: flex;
        justify-content: center;
        align-items: center;

        img {
            width: 100%;
            max-width: 600px;
            height: auto;
            border-radius: 16px;
            box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
        }
    }

    .service-process-section {
        margin-bottom: 48px;

        &:last-child {
            margin-bottom: 0;
        }
    }

    .development-process-section {
        margin-bottom: 48px;
    }

    .timeline-steps {
        max-width: 1000px;
        margin: 0 auto;
        position: relative;
    }

    .timeline-step {
        display: flex;
        gap: 32px;
        margin-bottom: 64px;
        position: relative;

        &:last-child {
            margin-bottom: 0;

            &::after {
                display: none;
            }
        }

        &::after {
            content: '';
            position: absolute;
            left: 23px;
            top: 48px;
            width: 2px;
            height: calc(100% + 16px);
            background: linear-gradient(180deg, #15803d 0%, #d1fae5 100%);
        }

        @media (max-width: 768px) {
            flex-direction: column;
            gap: 16px;
        }
    }

    .timeline-number {
        width: 48px;
        height: 48px;
        background: linear-gradient(135deg, #15803d 0%, #16a34a 100%);
        color: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 24px;
        font-weight: 700;
        flex-shrink: 0;
        box-shadow: 0 4px 12px rgba(21, 128, 61, 0.3);
        position: relative;
        z-index: 1;
    }

    .timeline-content {
        flex: 1;
        display: flex;
        gap: 24px;
        align-items: flex-start;

        @media (max-width: 768px) {
            flex-direction: column;
        }
    }

    .timeline-image {
        width: 120px;
        height: 120px;
        flex-shrink: 0;
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        background: #f9fafb;
        display: flex;
        align-items: center;
        justify-content: center;

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }

        @media (max-width: 768px) {
            width: 100px;
            height: 100px;
        }
    }

    .timeline-text {
        flex: 1;
    }

    .timeline-title {
        font-size: 24px;
        font-weight: 600;
        color: #0a6b2e;
        margin-bottom: 8px;

        @media (max-width: 768px) {
            font-size: 20px;
        }
    }

    .timeline-desc {
        font-size: 18px;
        font-weight: 500;
        color: #374151;
        margin-bottom: 8px;
        line-height: 1.5;

        @media (max-width: 768px) {
            font-size: 16px;
        }
    }

    .timeline-detail {
        font-size: 16px;
        font-weight: 300;
        color: #6b7280;
        line-height: 1.6;

        @media (max-width: 768px) {
            font-size: 14px;
        }
    }

    .process-main-title {
        font-size: 36px;
        font-weight: 700;
        color: #0a6b2e;
        text-align: center;
        margin-bottom: 16px;

        @media (max-width: 768px) {
            font-size: 28px;
        }
    }

    .process-description {
        font-size: 18px;
        color: #6b7280;
        text-align: center;
        max-width: 800px;
        margin-left: auto;
        margin-right: auto;
    }

    .steps-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 24px;

        @media (max-width: 1024px) {
            grid-template-columns: repeat(2, 1fr);
        }

        @media (max-width: 640px) {
            grid-template-columns: 1fr;
        }
    }

    .step-card {
        background: white;
        overflow: hidden;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        transition: all 0.3s ease;
        position: relative;

        &:hover {
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);

            .step-overlay {
                bottom: 0;
            }

            .step-overlay-text {
                opacity: 1;
            }
        }
    }

    .step-header-bar {
        background: linear-gradient(135deg, #15803d 0%, #15803d 100%);
        padding: 20px 24px;
        display: flex;
        align-items: center;
        gap: 16px;
        transition: border-radius 0.3s ease;
    }

    .step-number {
        width: 48px;
        height: 48px;
        background: white;
        color: #16a34a;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 24px;
        font-weight: 700;
        flex-shrink: 0;
    }

    .step-title {
        font-size: 24px;
        font-weight: 600;
        color: white;
        margin: 0;
        flex: 1;
        line-height:1;
        @media (max-width: 768px) {
            font-size: 18px;
        }
    }

    .step-image-container {
        position: relative;
        width: 100%;
        height: 240px;
        overflow: hidden;
    }

    .step-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }

    .step-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 100%;
        background: #15803d;
        display: flex;
        align-items: flex-start;
        justify-content: flex-start;
        transition: bottom 0.4s ease;
        overflow: hidden;
    }

    .step-overlay-content {
        width: 100%;
        padding-left: 20px;
        opacity: 0;
        transition: opacity 0.3s ease 0.2s;
    }

    .step-card:hover .step-overlay-content {
        opacity: 1;
    }

    .overlay-intro {
        color: white;
        font-size: 20px;
        font-weight: 600;
        text-align: left;
    }

    .overlay-list {
        list-style: none;
        padding: 0;
        text-align: left;
        padding-left:12px;
        li {
            color: white;
            font-size: 18px;
            padding-left: 0;
        }
    }

    .overlay-note {
        color: white;
        font-size: 16px;
        margin: 0;
        text-align: left;
        font-weight: 400;
    }

    .step-overlay-text {
        color: white;
        font-size: 16px;
        line-height: 1.6;
        text-align: center;
        margin: 0;
        opacity: 0;
        transition: opacity 0.3s ease 0.2s;
    }

    .step-card:hover .step-overlay-text {
        opacity: 1;
    }

    .get-started-btn {
        display: inline-block;
        background-color: #FBBF24;
        color: #1F2937;
        font-weight: bold;
        padding: 16px 48px;
        border-radius: 9999px;
        transition: all 0.3s ease;
        font-size: 18px;
        text-decoration: none;
        box-shadow: 0 4px 12px rgba(251, 191, 36, 0.4);

        &:hover {
            background-color: #F59E0B;
            transform: translateY(-2px);
            box-shadow: 0 6px 16px rgba(251, 191, 36, 0.5);
        }
    }

    .logistics-service-section {
        margin-bottom: 48px;
    }

    .shipping-intro {
        font-size: 18px;
        font-weight: 500;
        color: #374151;
        text-align: center;
        margin-bottom: 40px;
        max-width: 800px;
        margin-left: auto;
        margin-right: auto;
    }

    .shipping-options-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 32px;
        margin-bottom: 64px;

        @media (max-width: 1024px) {
            grid-template-columns: 1fr;
            gap: 24px;
        }
    }

    .shipping-card {
        background: white;
        border: 2px solid #e5e7eb;
        border-radius: 16px;
        padding: 32px;
        transition: all 0.3s ease;
        position: relative;

        &:hover {
            border-color: #15803d;
            box-shadow: 0 12px 24px rgba(21, 128, 61, 0.15);
            transform: translateY(-4px);
        }

        &.featured {
            border-color: #15803d;
            background: linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%);
            box-shadow: 0 8px 16px rgba(21, 128, 61, 0.1);
        }
    }

    .featured-badge {
        position: absolute;
        top: -12px;
        right: 24px;
        background: linear-gradient(135deg, #15803d 0%, #16a34a 100%);
        color: white;
        padding: 6px 16px;
        border-radius: 20px;
        font-size: 14px;
        font-weight: 600;
        box-shadow: 0 4px 8px rgba(21, 128, 61, 0.3);
    }

    .shipping-icon {
        font-size: 48px;
        text-align: center;
        margin-bottom: 16px;
    }

    .shipping-title {
        font-size: 28px;
        font-weight: 700;
        color: #0a6b2e;
        text-align: center;
        margin-bottom: 8px;

        @media (max-width: 768px) {
            font-size: 24px;
        }
    }

    .shipping-subtitle {
        font-size: 16px;
        font-weight: 600;
        color: #16a34a;
        text-align: center;
        margin-bottom: 16px;
    }

    .shipping-description {
        font-size: 16px;
        color: #6b7280;
        text-align: center;
        line-height: 1.6;
        margin-bottom: 24px;
        padding-bottom: 24px;
        border-bottom: 1px solid #e5e7eb;
    }

    .shipping-details {
        text-align: left;
    }

    .details-heading {
        font-size: 18px;
        font-weight: 600;
        color: #374151;
        margin-bottom: 12px;
        margin-top: 20px;

        &:first-child {
            margin-top: 0;
        }
    }

    .details-list {
        list-style: none;
        padding: 0;
        margin-bottom: 16px;

        li {
            font-size: 15px;
            color: #6b7280;
            padding-left: 24px;
            margin-bottom: 8px;
            position: relative;
            line-height: 1.5;

            &::before {
                content: '•';
                position: absolute;
                left: 8px;
                color: #15803d;
                font-weight: 700;
            }
        }
    }

    .shipping-note {
        font-size: 14px;
        color: #6b7280;
        font-style: italic;
        line-height: 1.6;
        margin-top: 16px;
        padding: 16px;
        background: #f9fafb;
        border-radius: 8px;
        border-left: 3px solid #15803d;
    }

    .shipping-guide {
        background: linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%);
        border-radius: 16px;
        padding: 40px;
        margin-bottom: 48px;
        border: 2px solid #15803d;

        @media (max-width: 768px) {
            padding: 24px;
        }
    }

    .guide-title {
        font-size: 28px;
        font-weight: 700;
        color: #0a6b2e;
        text-align: center;
        margin-bottom: 32px;

        @media (max-width: 768px) {
            font-size: 24px;
        }
    }

    .guide-grid {
        display: grid;
        gap: 20px;
        margin-bottom: 24px;
    }

    .guide-item {
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 16px;
        background: white;
        border-radius: 8px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

        @media (max-width: 768px) {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
        }
    }

    .guide-label {
        font-size: 16px;
        font-weight: 600;
        color: #15803d;
        min-width: 220px;

        @media (max-width: 768px) {
            min-width: auto;
        }
    }

    .guide-value {
        font-size: 16px;
        color: #374151;
        flex: 1;
    }

    .guide-note {
        font-size: 15px;
        color: #6b7280;
        text-align: center;
        font-style: italic;
        margin: 0;
    }

    .global-shipping {
        background: white;
        border-radius: 16px;
        padding: 40px;
        border: 2px solid #e5e7eb;

        @media (max-width: 768px) {
            padding: 24px;
        }
    }

    .global-title {
        font-size: 28px;
        font-weight: 700;
        color: #0a6b2e;
        text-align: center;
        margin-bottom: 16px;

        @media (max-width: 768px) {
            font-size: 24px;
        }
    }

    .global-description {
        font-size: 16px;
        color: #6b7280;
        text-align: center;
        line-height: 1.6;
        margin-bottom: 32px;
        max-width: 800px;
        margin-left: auto;
        margin-right: auto;
    }

    .global-features {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 20px;
        margin-bottom: 24px;

        @media (max-width: 768px) {
            grid-template-columns: 1fr;
        }
    }

    .global-feature {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 16px;
        background: #f9fafb;
        border-radius: 8px;
    }

    .feature-icon {
        width: 32px;
        height: 32px;
        background: #15803d;
        color: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
        font-size: 18px;
        flex-shrink: 0;
    }

    .feature-text {
        font-size: 16px;
        color: #374151;
        font-weight: 500;
    }

    .global-footer {
        font-size: 16px;
        color: #6b7280;
        text-align: center;
        font-weight: 500;
        margin: 0;
    }


    // Service Collaboration 背景图片样式
    .collaboration-card {
        overflow: hidden;
        
        .step-content {
            padding: 0;
        }
    }

    .collaboration-background {
        position: relative;
        width: 100%;
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 60px 40px;
        height:328px;
        @media (max-width: 768px) {
            min-height: 300px;
            padding: 40px 24px;
        }
    }

    .collaboration-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background:  rgb(21, 128, 61);
        z-index: 1;
    }

    .collaboration-content {
        position: relative;
        z-index: 2;
        text-align: center;
        color: white;
        max-width: 800px;
        margin: 0 auto;
    }

    .collaboration-icon {
        font-size: 64px;
        margin-bottom: 24px;
        line-height: 1;
        animation: float 3s ease-in-out infinite;

        @media (max-width: 768px) {
            font-size: 48px;
            margin-bottom: 16px;
        }
    }

    @keyframes float {
        0%, 100% {
            transform: translateY(0);
        }
        50% {
            transform: translateY(-10px);
        }
    }

    .collaboration-title {
        font-size: 48px;
        font-weight: 700;
        color: white;
        margin: 0 0 16px 0;
        text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
        line-height: 1.2;

        @media (max-width: 768px) {
            font-size: 32px;
            margin-bottom: 12px;
        }
    }

    .collaboration-subtitle {
        font-size: 24px;
        font-weight: 500;
        color: rgba(255, 255, 255, 0.95);
        margin: 0;
        text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.2);
        line-height: 1.4;

        @media (max-width: 768px) {
            font-size: 18px;
        }
    }
</style>
